// ─────────────────────────────────────────────────────────────
// ParentalConsentModal.js
//
// IMPORTANT DESIGN DECISION:
// When a form is submitted, we save a `formSnapshot` field to
// Firestore containing the FULL rendered HTML of the form as
// the user saw it, with fields filled in. This means if the
// verbiage ever changes, admins can still see exactly what each
// person agreed to. The View Release display renders from the
// snapshot, not from the current template.
// ─────────────────────────────────────────────────────────────

import React, { useState } from 'react';
import { db } from '../firebase';
import { collection, addDoc, Timestamp } from 'firebase/firestore';
import SITE from '../site';

// ─── FORM VERSION ─────────────────────────────────────────────
// Increment this if the form text changes materially, so records
// can be grouped by which version of the form was agreed to.
const FORM_VERSION = '1.0';

// ─── FORM TEMPLATE ────────────────────────────────────────────
// This function returns the canonical form HTML given filled-in
// field values. It is used both to render the live form AND to
// generate the snapshot saved at submission time.
// Do not change this without incrementing FORM_VERSION.
const buildFormHtml = ({ participantName, parentName, phone, signature, dateStr }) => `
<div class="consent-document">
  <h2 class="consent-title">Parental Consent (Minor Participant)</h2>

  <div class="consent-field-row">
    <span class="consent-label">Participant name:</span>
    <span class="consent-field-value">${participantName || '___________________________'}</span>
  </div>
  <div class="consent-field-row">
    <span class="consent-label">Parent / Guardian name:</span>
    <span class="consent-field-value">${parentName || '___________________________'}</span>
  </div>
  <div class="consent-field-row">
    <span class="consent-label">Phone (for emergencies):</span>
    <span class="consent-field-value">${phone || '___________________________'}</span>
  </div>

  <div class="consent-body">
    <p>
      I am the parent or legal guardian of the above-named participant and give permission
      for them to participate in this community-organized pickup frisbee game.
    </p>
    <p>
      I understand that participation in athletic activities involves inherent risks,
      including but not limited to physical injury, falls, collisions with other players,
      and exposure to weather conditions. I accept these risks on behalf of my child and
      agree that they participate voluntarily.
    </p>
    <p>
      In the event of an emergency and I cannot be reached, I authorize participants and
      organizers present to seek appropriate medical care for my child. I agree to be
      responsible for any costs incurred.
    </p>
    <p>
      By signing below, I confirm I have read and understood this consent form.
    </p>
  </div>

  <div class="consent-field-row consent-sig-row">
    <span class="consent-label">Signature:</span>
    <span class="consent-field-value consent-signature">${signature || '___________________________'}</span>
    <span class="consent-label" style="margin-left:2rem">Date:</span>
    <span class="consent-field-value">${dateStr || '___________________________'}</span>
  </div>

  <div class="consent-footer-note">
    Form version ${FORM_VERSION} · Submitted electronically via ${SITE.domain}
  </div>
</div>
`;

function ParentalConsentModal({ onClose }) {
  const [participantName, setParticipantName] = useState('');
  const [parentName, setParentName]           = useState('');
  const [phone, setPhone]                     = useState('');
  const [signature, setSignature]             = useState('');
  const [submitting, setSubmitting]           = useState(false);
  const [submitted, setSubmitted]             = useState(false);
  const [error, setError]                     = useState('');

  const today = new Date().toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric'
  });

  const validate = () => {
    if (!participantName.trim()) return 'Participant name is required.';
    if (!parentName.trim())      return 'Parent / Guardian name is required.';
    if (!phone.trim())           return 'Phone number is required.';
    if (!signature.trim())       return 'Signature is required.';
    if (signature.trim().toLowerCase() !== parentName.trim().toLowerCase())
      return 'Signature must match the Parent / Guardian name above.';
    return null;
  };

  const handleSubmit = async () => {
    const err = validate();
    if (err) { setError(err); return; }
    setSubmitting(true);
    setError('');
    try {
      // Build the exact HTML snapshot the user saw with fields filled in
      const snapshot = buildFormHtml({
        participantName: participantName.trim(),
        parentName:      parentName.trim(),
        phone:           phone.trim(),
        signature:       signature.trim(),
        dateStr:         today,
      });

      const releaseRef = await addDoc(collection(db, 'releases'), {
        // Individual fields for table display in Admin
        participantName: participantName.trim(),
        parentName:      parentName.trim(),
        phone:           phone.trim(),
        signature:       signature.trim(),
        dateStr:         today,
        formVersion:     FORM_VERSION,
        // Full snapshot — this is what View Release renders
        formSnapshot:    snapshot,
        submittedAt:     Timestamp.now(),
      });
      try {
        await addDoc(collection(db, 'auditLogs'), {
          actorType: 'user',
          actorId: null,
          actorName: parentName.trim(),
          action: 'release_created',
          entityType: 'release',
          entityId: releaseRef.id,
          entityLabel: participantName.trim(),
          before: null,
          after: { participantName: participantName.trim(), parentName: parentName.trim() },
          timestamp: Timestamp.now()
        });
      } catch (e) { console.warn('Could not log release creation:', e); }

      setSubmitted(true);
    } catch (e) {
      console.error(e);
      setError('Error saving form. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-box">
        <button className="modal-close" onClick={onClose}>×</button>

        {submitted ? (
          <div className="modal-submitted">
            <div className="modal-submitted-icon">✓</div>
            <h2>Thank you!</h2>
            <p>Your consent form has been submitted and recorded.</p>
            <button className="submit-btn" onClick={onClose}>Close</button>
          </div>
        ) : (
          <>
            {/* Render the live form using the same template function */}
            <div dangerouslySetInnerHTML={{ __html: buildFormHtml({
              participantName: '',
              parentName: '',
              phone: '',
              signature: '',
              dateStr: today,
            })}} style={{display:'none'}} />

            {/* Actual interactive form */}
            <div className="consent-document">
              <h2 className="consent-title">Parental Consent (Minor Participant)</h2>

              <div className="consent-field-row">
                <span className="consent-label">Participant name:</span>
                <input className="consent-input" type="text"
                  value={participantName}
                  onChange={e => setParticipantName(e.target.value)}
                  placeholder="Child's full name" />
              </div>
              <div className="consent-field-row">
                <span className="consent-label">Parent / Guardian name:</span>
                <input className="consent-input" type="text"
                  value={parentName}
                  onChange={e => setParentName(e.target.value)}
                  placeholder="Your full name" />
              </div>
              <div className="consent-field-row">
                <span className="consent-label">Phone (for emergencies):</span>
                <input className="consent-input" type="tel"
                  value={phone}
                  onChange={e => setPhone(e.target.value)}
                  placeholder="(360) 555-0100" />
              </div>

              <div className="consent-body">
                <p>
                  I am the parent or legal guardian of the above-named participant and give
                  permission for them to participate in this community-organized pickup frisbee
                  game.
                </p>
                <p>
                  I understand that participation in athletic activities involves inherent risks,
                  including but not limited to physical injury, falls, collisions with other
                  players, and exposure to weather conditions. I accept these risks on behalf of
                  my child and agree that they participate voluntarily.
                </p>
                <p>
                  In the event of an emergency and I cannot be reached, I authorize participants
                  and organizers present to seek appropriate medical care for my child. I agree
                  to be responsible for any costs incurred.
                </p>
                <p>
                  By signing below, I confirm I have read and understood this consent form.
                </p>
              </div>

              <div className="consent-field-row consent-sig-row">
                <span className="consent-label">Signature:</span>
                <input className="consent-input consent-sig-input" type="text"
                  value={signature}
                  onChange={e => setSignature(e.target.value)}
                  placeholder="Type your full name" />
                <span className="consent-label" style={{marginLeft:'1.5rem',
                  whiteSpace:'nowrap'}}>Date:</span>
                <span className="consent-date">{today}</span>
              </div>
              <div className="consent-sig-note">
                Type your full name above as your electronic signature. It must match the
                Parent / Guardian name field.
              </div>

              <div className="consent-footer-note">
                Form version {FORM_VERSION} · Submitted electronically via {SITE.domain}
              </div>

              {error && <div className="modal-error" style={{marginTop:'1rem'}}>{error}</div>}

              <button className="submit-btn" style={{marginTop:'1rem', width:'100%'}}
                onClick={handleSubmit} disabled={submitting}>
                {submitting ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default ParentalConsentModal;
